import { m } from 'framer-motion';
// @mui
import { Container, Typography } from '@mui/material';
// hooks
import useAuth, { UserRole } from '../hooks/useAuth';
// components
import { MotionContainer, varBounce } from '../components/animate';
// assets
import { ForbiddenIllustration } from '../assets';

// ----------------------------------------------------------------------

type RoleBasedGuardProp = {
  hasContent?: boolean;
  roles?: UserRole[];
  feature?: string; // Feature-based access control
  children: React.ReactNode;
};

export default function RoleBasedGuard({ 
  hasContent = true, 
  roles, 
  feature,
  children 
}: RoleBasedGuardProp) {
  const { getUserRole, hasRole, canAccess, isInitialized, isAuthenticated } = useAuth();

  // Wait for authentication to be initialized
  if (!isInitialized) {
    return null; // or a loading spinner
  }

  // If not authenticated, let AuthGuard handle it
  if (!isAuthenticated) {
    return null;
  }

  const currentRole = getUserRole();

  // If no role is set, deny access
  if (!currentRole) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center' }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" paragraph>
            Authentication Required
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            Please login to access this page
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
        </m.div>
      </Container>
    ) : null;
  }

  // Check role-based access
  if (roles && !hasRole(roles)) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center' }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" paragraph>
            Permission Denied
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            You do not have permission to access this page
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Required role(s): {roles.join(', ')}
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your role: {currentRole}
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
        </m.div>
      </Container>
    ) : null;
  }

  // Check feature-based access
  if (feature && !canAccess(feature)) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center' }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" paragraph>
            Access Restricted
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            You don't have access to this feature
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Feature: {feature}
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your role: {currentRole}
          </Typography>
        </m.div>
        <m.div variants={varBounce().in}>
          <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
        </m.div>
      </Container>
    ) : null;
  }

  return <>{children}</>;
}